import React, { useEffect, useState } from "react";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Container,
	Paper,
	Snackbar,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { BarChart3, Save } from "lucide-react";
import { supabase } from "../../config/supabase";
import { useAuth } from "../../contexts/AuthContext";

const DEFAULTS = {
	stat1_value: "196",
	stat1_label: "Schools",
	stat2_value: "60,000+",
	stat2_label: "Students",
	stat3_value: "95%",
	stat3_label: "Retention",
	stat4_value: "3",
	stat4_label: "States",
};

const STAT_FIELDS = [
	{ valueKey: "stat1_value", labelKey: "stat1_label", title: "Stat 1" },
	{ valueKey: "stat2_value", labelKey: "stat2_label", title: "Stat 2" },
	{ valueKey: "stat3_value", labelKey: "stat3_label", title: "Stat 3" },
	{ valueKey: "stat4_value", labelKey: "stat4_label", title: "Stat 4" },
];

const WebStats = () => {
	const { user } = useAuth();
	const [rowId, setRowId] = useState(null);
	const [form, setForm] = useState(DEFAULTS);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "success",
	});

	useEffect(() => {
		let cancelled = false;

		const load = async () => {
			setLoading(true);
			try {
				const { data, error } = await supabase
					.from("web_stats")
					.select("*")
					.order("updated_at", { ascending: false })
					.limit(1)
					.maybeSingle();

				if (error) throw error;
				if (!cancelled && data) {
					setRowId(data.id);
					setForm({
						stat1_value: data.stat1_value || DEFAULTS.stat1_value,
						stat1_label: data.stat1_label || DEFAULTS.stat1_label,
						stat2_value: data.stat2_value || DEFAULTS.stat2_value,
						stat2_label: data.stat2_label || DEFAULTS.stat2_label,
						stat3_value: data.stat3_value || DEFAULTS.stat3_value,
						stat3_label: data.stat3_label || DEFAULTS.stat3_label,
						stat4_value: data.stat4_value || DEFAULTS.stat4_value,
						stat4_label: data.stat4_label || DEFAULTS.stat4_label,
					});
				}
			} catch (err) {
				console.error("Failed to load web stats:", err);
				if (!cancelled) {
					setSnackbar({
						open: true,
						message: err.message || "Failed to load web stats",
						severity: "error",
					});
				}
			} finally {
				if (!cancelled) setLoading(false);
			}
		};

		load();
		return () => {
			cancelled = true;
		};
	}, []);

	const updateField = (key) => (e) => {
		setForm((prev) => ({ ...prev, [key]: e.target.value }));
	};

	const canSave =
		STAT_FIELDS.every(
			({ valueKey, labelKey }) =>
				(form[valueKey] || "").trim() && (form[labelKey] || "").trim(),
		) && !saving;

	const handleSave = async () => {
		if (!canSave) return;
		setSaving(true);
		try {
			const payload = {
				stat1_value: form.stat1_value.trim(),
				stat1_label: form.stat1_label.trim(),
				stat2_value: form.stat2_value.trim(),
				stat2_label: form.stat2_label.trim(),
				stat3_value: form.stat3_value.trim(),
				stat3_label: form.stat3_label.trim(),
				stat4_value: form.stat4_value.trim(),
				stat4_label: form.stat4_label.trim(),
				updated_at: new Date().toISOString(),
				updated_by: user?.id || null,
			};

			let error;
			if (rowId) {
				({ error } = await supabase
					.from("web_stats")
					.update(payload)
					.eq("id", rowId));
			} else {
				const { data, error: insertError } = await supabase
					.from("web_stats")
					.insert(payload)
					.select("id")
					.single();
				error = insertError;
				if (data?.id) setRowId(data.id);
			}

			if (error) throw error;

			setSnackbar({
				open: true,
				message: "Homepage stats saved.",
				severity: "success",
			});
		} catch (err) {
			console.error("Failed to save web stats:", err);
			setSnackbar({
				open: true,
				message: err.message || "Failed to save web stats",
				severity: "error",
			});
		} finally {
			setSaving(false);
		}
	};

	return (
		<Container maxWidth="md" sx={{ py: 4 }}>
			<Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
				<BarChart3 size={28} />
				<Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
					Web Stats
				</Typography>
			</Stack>
			<Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
				Update the four figures on the homepage cover (Schools, Students,
				Retention, States).
			</Typography>

			<Paper sx={{ p: 4, borderRadius: 2 }}>
				{loading ? (
					<Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
						<CircularProgress />
					</Box>
				) : (
					<Stack spacing={3}>
						{STAT_FIELDS.map(({ valueKey, labelKey, title }) => (
							<Box key={valueKey}>
								<Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
									{title}
								</Typography>
								<Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
									<TextField
										label="Value"
										value={form[valueKey]}
										onChange={updateField(valueKey)}
										placeholder="e.g. 196"
										fullWidth
									/>
									<TextField
										label="Label"
										value={form[labelKey]}
										onChange={updateField(labelKey)}
										placeholder="e.g. Schools"
										fullWidth
									/>
								</Stack>
							</Box>
						))}

						<Box
							sx={{
								p: 2,
								borderRadius: 2,
								bgcolor: "grey.900",
								color: "common.white",
								display: "grid",
								gridTemplateColumns: "repeat(4, 1fr)",
								gap: 1,
								textAlign: "center",
							}}>
							{STAT_FIELDS.map(({ valueKey, labelKey }) => (
								<Box key={`preview-${valueKey}`}>
									<Typography sx={{ fontWeight: 800, fontSize: 22 }}>
										{form[valueKey] || "—"}
									</Typography>
									<Typography
										sx={{
											fontSize: 11,
											letterSpacing: "0.08em",
											textTransform: "uppercase",
											opacity: 0.7,
										}}>
										{form[labelKey] || "—"}
									</Typography>
								</Box>
							))}
						</Box>

						<Box>
							<Button
								variant="contained"
								color="primary"
								size="large"
								startIcon={
									saving ? <CircularProgress size={16} /> : <Save size={18} />
								}
								onClick={handleSave}
								disabled={!canSave}>
								{saving ? "Saving..." : "Save stats"}
							</Button>
						</Box>
					</Stack>
				)}
			</Paper>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={() => setSnackbar({ ...snackbar, open: false })}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
				<Alert
					severity={snackbar.severity}
					onClose={() => setSnackbar({ ...snackbar, open: false })}
					sx={{ width: "100%" }}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Container>
	);
};

export default WebStats;
